/**
 * Detecting a microphone that is open but not actually capturing.
 *
 * WHAT THIS DOES NOT MEASURE: whether the visitor is talking. A banner that
 * fires because someone paused to think would be worse than no banner at
 * all — it trains people to ignore banners, and the next one might matter.
 *
 * The signal is the noise floor. A working microphone in a quiet room still
 * captures room tone: fans, the street, the room's own hiss. That sits well
 * above digital zero. A microphone that is hardware-muted, pointed at a
 * disconnected input, or handed to us by an OS that is feeding a dead device
 * produces something much closer to true silence, and keeps producing it.
 *
 * So the question asked here is "is this capture path dead?", not "has this
 * person spoken?", and a visitor who has not started talking yet is above
 * threshold the entire time on room tone alone.
 *
 * THRESHOLD (-66 dBFS, RMS 0.0005). Chosen to sit in the gap between those
 * two populations: a treated recording booth still floors around -60 dBFS,
 * while a muted or dead capture path reads at or near zero. Sitting just
 * below the quietest realistic room keeps false positives near zero, and
 * being above exact zero keeps it honest for low-gain devices that emit
 * tiny nonzero dither rather than literal silence.
 *
 * WINDOW (4s). Long enough that a device still ramping up after being
 * granted, a buffer hiccup, or a switch between inputs does not trip it —
 * those resolve in well under a second. Short enough that someone who
 * genuinely has a dead mic is told before they conclude the demo is broken,
 * which is the failure this whole module exists to prevent. Any sample above
 * threshold resets the clock to zero, so the 4 seconds must be consecutive.
 */

const DEFAULT_THRESHOLD_RMS = 0.0005;
const DEFAULT_WINDOW_MS = 4000;
const DEFAULT_INTERVAL_MS = 250;

/** Root-mean-square of a float time-domain buffer, in the 0..1 range. */
export function rmsOf(buffer: Float32Array): number {
  if (buffer.length === 0) return 0;
  let sum = 0;
  for (const sample of buffer) sum += sample * sample;
  return Math.sqrt(sum / buffer.length);
}

export interface Sampler {
  /** Current RMS, or null when the level cannot be read. */
  readonly sample: () => number | null;
  readonly dispose: () => void;
}

type AudioContextCtor = new () => AudioContext;

/**
 * Build a level sampler over a live stream via WebAudio.
 *
 * Returns null when WebAudio is unavailable, which makes the monitor a
 * no-op rather than a guess — never banner on missing instrumentation.
 */
export function createStreamSampler(stream: MediaStream): Sampler | null {
  const g = globalThis as unknown as {
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  };
  const Ctor = g.AudioContext ?? g.webkitAudioContext;
  if (Ctor === undefined) return null;

  const ctx = new Ctor();
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 2048;
  ctx.createMediaStreamSource(stream).connect(analyser);
  const buffer = new Float32Array(analyser.fftSize);

  return {
    sample: (): number => {
      analyser.getFloatTimeDomainData(buffer);
      return rmsOf(buffer);
    },
    dispose: (): void => {
      analyser.disconnect();
      void ctx.close();
    },
  };
}

export interface SilenceMonitorOptions {
  readonly sampler: Sampler;
  /** Fired once per silent spell, when the window elapses. */
  readonly onSilent: () => void;
  /** Fired when level returns after `onSilent` fired. */
  readonly onSound?: () => void;
  /** True only while the session is genuinely capturing. */
  readonly isListening: () => boolean;
  readonly thresholdRms?: number;
  readonly windowMs?: number;
  readonly intervalMs?: number;
}

/**
 * Watch a sampler and report a dead capture path. Returns a cleanup.
 *
 * The clock only advances while `isListening()` holds, so time spent with
 * voice off never counts toward the window.
 */
export function startSilenceMonitor(opts: SilenceMonitorOptions): () => void {
  const threshold = opts.thresholdRms ?? DEFAULT_THRESHOLD_RMS;
  const windowMs = opts.windowMs ?? DEFAULT_WINDOW_MS;
  const intervalMs = opts.intervalMs ?? DEFAULT_INTERVAL_MS;

  let silentForMs = 0;
  let reported = false;

  const timer = setInterval(() => {
    if (!opts.isListening()) {
      silentForMs = 0;
      return;
    }
    const level = opts.sampler.sample();
    // Unreadable level is not evidence of silence.
    if (level === null) return;

    if (level >= threshold) {
      silentForMs = 0;
      if (reported) {
        reported = false;
        opts.onSound?.();
      }
      return;
    }

    silentForMs += intervalMs;
    if (silentForMs >= windowMs && !reported) {
      reported = true;
      opts.onSilent();
    }
  }, intervalMs);

  return (): void => {
    clearInterval(timer);
    opts.sampler.dispose();
  };
}
